import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { SiteCheckDimension } from 'app/account-settings/models/site-check.model';
import {
  SITE_CHECK_KEY_PREFIX,
  dimensionLabelKey,
  joinNames,
  reasonIn,
  toDate,
} from 'app/account-settings/site-check.utils';
import { CATEGORY_LEVEL_OPTIONS } from 'app/data-platform/category-levels.utils';
import { FlyoutOpeningData } from '../flyout-models';
import { FlyoutService } from '../flyout.service';

export const STRUCTURED_DATA_HELP_URL = 'https://developers.google.com/search/docs/appearance/structured-data/product';

const MAX_EXAMPLES = 5;
const CATEGORY_TYPE = 'category';
const PRODUCT_KIND = 'product';
const OVER_TIER_LIMIT = 'over-tier-limit';
// The tracking script reads these from Product and BreadcrumbList structured data.
const MARKUP_TYPES = ['brand', 'gender', 'material', CATEGORY_TYPE];
const MISSING_REASONS = ['not-found', 'no-category-path'];

export interface SiteCheckRowView {
  type: string;
  labelKey: string;
  reasonCode: string;
  reasonText: string | null;
  reasonKey: string;
  examples: string[];
  isActive: boolean;
  level: number | null;
}

export interface SiteCheckChangesView {
  text: string;
  hasChanges: boolean;
}

@Component({
  selector: 'app-flyout-site-check',
  templateUrl: './flyout-site-check.component.html',
  styleUrl: './flyout-site-check.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlyoutSiteCheckComponent {
  public readonly flyoutOpeningData = input.required<FlyoutOpeningData>();
  public readonly structuredDataHelpUrl = STRUCTURED_DATA_HELP_URL;

  private readonly flyoutService = inject(FlyoutService);
  private readonly translate = inject(TranslateService);
  private readonly language = toSignal(this.translate.onLangChange.pipe(map((event) => event.lang)), {
    initialValue: this.translate.currentLang,
  });

  private readonly status = computed(() => this.flyoutOpeningData().siteCheckStatus ?? null);
  private readonly result = computed(() => this.status()?.result ?? null);
  private readonly recommendations = computed(() => this.result()?.recommendations ?? null);
  private readonly active = computed(() => this.flyoutOpeningData().siteCheckActiveDimensions ?? []);
  private readonly applied = computed(() => this.flyoutOpeningData().siteCheckAppliedDimensions ?? []);
  private readonly recommendedLevel = computed(() => {
    const level = this.recommendations()?.categoryLevel.recommendedLevel ?? null;
    return level !== null && CATEGORY_LEVEL_OPTIONS.includes(level) ? level : null;
  });
  private readonly levelChange = computed(() => {
    const level = this.recommendedLevel();
    const applied = this.applied();
    const categoryChecked = applied.length ? applied.includes(CATEGORY_TYPE) : this.active().includes(CATEGORY_TYPE);
    return level !== null && categoryChecked && level !== this.flyoutOpeningData().siteCheckCategoryLevel
      ? level
      : null;
  });

  public readonly checkedAt = computed(() => toDate(this.status()?.checkedAt));
  public readonly pagesChecked = computed(
    () => this.result()?.pages.filter((page) => page.outcome === 'ok').length ?? 0,
  );
  public readonly activateRows = computed(() => this.rows((dimension) => this.applied().includes(dimension.type)));
  public readonly skipRows = computed(() => this.rows((dimension) => !this.applied().includes(dimension.type)));
  public readonly missingDataNames = computed<string | null>(() => {
    const productPagesRead = !!this.result()?.pages.some((page) => page.outcome === 'ok' && page.kind === PRODUCT_KIND);
    if (!productPagesRead) {
      return null;
    }
    const names = this.skipRows()
      .filter((row) => MARKUP_TYPES.includes(row.type) && MISSING_REASONS.includes(row.reasonCode))
      .map((row) => this.translate.instant(row.labelKey));
    return names.length ? joinNames(names, this.language()) : null;
  });
  public readonly changes = computed<SiteCheckChangesView>(() => {
    const active = this.active();
    const applied = this.applied();
    const language = this.language();
    const names = (types: string[]): string =>
      joinNames(
        types.map((type) => this.translate.instant(dimensionLabelKey(type))),
        language,
      );
    const activates = applied.filter((type) => !active.includes(type));
    const deactivates = applied.length ? active.filter((type) => !applied.includes(type)) : [];
    const level = this.levelChange();
    const parts: string[] = [];
    if (activates.length) {
      parts.push(this.translate.instant(`${SITE_CHECK_KEY_PREFIX}.changes-activate`, { names: names(activates) }));
    }
    if (deactivates.length) {
      parts.push(this.translate.instant(`${SITE_CHECK_KEY_PREFIX}.changes-deactivate`, { names: names(deactivates) }));
    }
    if (level !== null) {
      parts.push(this.translate.instant(`${SITE_CHECK_KEY_PREFIX}.changes-level`, { level }));
    }
    return {
      text: parts.length ? parts.join(' · ') : this.translate.instant(`${SITE_CHECK_KEY_PREFIX}.changes-none`),
      hasChanges: parts.length > 0,
    };
  });

  public close(): void {
    this.flyoutService.close();
  }

  public apply(): void {
    this.flyoutService.applySiteCheckRecommendations();
  }

  private rows(include: (dimension: SiteCheckDimension) => boolean): SiteCheckRowView[] {
    const language = this.language();
    const applied = this.applied();
    const active = this.active();
    const level = this.recommendedLevel();
    const levelExamples =
      this.recommendations()?.categoryLevel.levels.find((option) => option.level === level)?.examples ?? [];
    return (this.recommendations()?.dimensions ?? []).filter(include).map((dimension) => {
      const isApplied = applied.includes(dimension.type);
      // The model's reason argues for activating it, but the plan limit in the form leaves it out.
      const leftOut = dimension.recommended && !isApplied;
      const isCategory = dimension.type === CATEGORY_TYPE;
      return {
        type: dimension.type,
        labelKey: dimensionLabelKey(dimension.type),
        reasonCode: dimension.reasonCode,
        reasonText: leftOut ? null : reasonIn(dimension.reason, language),
        reasonKey: `${SITE_CHECK_KEY_PREFIX}.reasons.${leftOut ? OVER_TIER_LIMIT : dimension.reasonCode}`,
        examples: (isCategory && levelExamples.length ? levelExamples : dimension.examples).slice(0, MAX_EXAMPLES),
        isActive: active.includes(dimension.type),
        level: isCategory && isApplied ? level : null,
      };
    });
  }
}
